import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, MinusCircle } from 'lucide-react';
import { apiClient } from '../services/api';
import { useCampaignStore } from '../stores/campaignStore';
import { useVoucherStore } from '../stores/voucherStore';
import { CampaignHeader } from '../components/CampaignHeader';
import { useAnalytics } from '../hooks/useAnalytics';

export function ConsultVoucher() {
  const navigate = useNavigate();
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { campaign } = useCampaignStore();
  const { updateReservation, updateCustomer } = useVoucherStore();
  const { trackException, trackSelectContent } = useAnalytics();

  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const maskCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    let masked = digits;
    if (digits.length > 3) masked = digits.slice(0, 3) + '.' + digits.slice(3);
    if (digits.length > 6) masked = masked.slice(0, 7) + '.' + digits.slice(6);
    if (digits.length > 9) masked = masked.slice(0, 11) + '-' + digits.slice(9);
    return masked;
  };

  const handleChange = (value: string) => {
    setCpf(maskCpf(value));
    setNotFound(false);
    setError('');
  };

  const handleConsult = async () => {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11 || !campaign?.id) return;
    trackSelectContent('button', 'consultar_voucher', {
      eventCategory: 'consulta_voucher',
      eventAction: 'consultar_voucher',
      eventLabel: 'botao_consultar_voucher',
    });

    setLoading(true);
    setNotFound(false);
    setError('');

    try {
      const { data } = await apiClient.get(`/customer/campaigns/${campaign.id}/check-cpf`, {
        params: { cpf: digits },
      });

      if (data.hasReservation) {
        // Reserved → go to confirmation page
        updateReservation({
          storeName: data.storeName,
          storeCity: data.storeCity,
          storeState: data.storeState,
          scheduledDate: data.redemptionDate,
          voucherCode: data.voucherCode,
          qrCodeToken: data.qrCodeToken,
        });
        updateCustomer({ cpf: digits });
        navigate(`/${campaignSlug}/confirmacao`);
      } else if (data.hasRedemption) {
        // Redeemed → go to redeemed page
        updateReservation({
          storeName: data.storeName,
          storeCity: data.storeCity,
          storeState: data.storeState,
          scheduledDate: data.redemptionDate,
          voucherCode: data.voucherCode,
          redeemedAt: data.redeemedAt,
        });
        updateCustomer({ cpf: digits });
        navigate(`/${campaignSlug}/voucher-resgatado`);
      } else {
        // Not found
        setNotFound(true);
      }
    } catch {
      trackException('voucher_consultation_failed');
      setError('Erro ao consultar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CampaignHeader />

      <main className="max-w-lg mx-auto w-full px-6 pt-8 pb-6 flex-1">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Consultar Voucher</h1>
        <p className="text-sm text-gray-500 mb-6">
          Informe seu CPF para verificar se você possui uma reserva ativa nesta campanha.
        </p>

        <div className="mb-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CPF <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={cpf}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="000.000.000-00"
            maxLength={14}
            className="input-field"
          />
        </div>

        <button
          onClick={() => void handleConsult()}
          disabled={cpf.replace(/\D/g, '').length !== 11 || loading}
          className="btn-primary w-full mt-5 uppercase tracking-wide font-bold"
        >
          {loading ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Consultar'}
        </button>

        {error && <p className="text-sm text-red-600 text-center mt-4">{error}</p>}

        {notFound && (
          <div className="mt-6 border border-gray-200 rounded-2xl p-6 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <MinusCircle size={24} className="text-gray-400" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Nenhuma reserva encontrada</h3>
            <p className="text-sm text-gray-500">
              Não encontramos uma reserva ativa para este CPF. Se desejar participar, volte à tela inicial e faça seu cadastro.
            </p>
          </div>
        )}
      </main>

      <footer className="max-w-lg mx-auto w-full px-6 pb-6">
        <button
          onClick={() => navigate(`/${campaignSlug}`)}
          className="btn-secondary w-full"
        >
          Voltar ao início
        </button>
      </footer>
    </div>
  );
}
